<template>
    <div class="login-container" :style="containerStyle">
        <a-card class="login-card">
            <template #title>
                <a-breadcrumb separator=">">
                    <a-breadcrumb-item>Login</a-breadcrumb-item>
                </a-breadcrumb>
            </template>

            <a-form
                :model="form"
                :rules="rules"
                ref="formRef"
                layout="vertical"
                @finish="handleSubmit"
            >
                <a-form-item label="E-mail" name="email">
                    <a-input
                        v-model:value="form.email"
                        placeholder="Digite seu e-mail"
                        autocomplete="username"
                    />
                </a-form-item>

                <a-form-item label="Senha" name="password">
                    <a-input-password
                        v-model:value="form.password"
                        placeholder="Digite sua senha"
                        autocomplete="current-password"
                    />
                </a-form-item>

                <a-form-item>
                    <a-button
                        type="primary"
                        html-type="submit"
                        :loading="loading"
                        block
                    >
                        Entrar
                    </a-button>
                </a-form-item>
            </a-form>

            <a-divider v-if="showAlternativeLogins">ou</a-divider>

            <div v-if="showAlternativeLogins" class="alternative-logins">
                <a-button v-if="eauthEnabled" block @click="loginWithEauth">
                    Entrar com EAuth
                </a-button>
                <a-button v-if="crmAuthEnabled" block @click="loginWithCrm">
                    Entrar com CRM
                </a-button>
            </div>
        </a-card>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { notification } from "ant-design-vue";
import {getCoords, isLocationActive} from "../../../../utils/userLocation";

const formRef = ref(null);
const loading = ref(false);
const loginBackground = ref('/img/background-login.jpg');

const containerStyle = computed(() => ({
    backgroundImage: `url('${loginBackground.value}')`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    backgroundSize: 'cover',
}));

async function loadLoginBackground() {
    try {
        const { data } = await window.axios.get('/api/configurations/personalizations/public');
        if (data?.data?.login_background) {
            loginBackground.value = data.data.login_background;
        }
    } catch (_) {
        // Mantém imagem padrão.
    }
}

onMounted(() => {
    loadLoginBackground();
    if (window.loginError) {
        notification.error({
            message: "Não foi possível entrar com o CRM",
            description: String(window.loginError),
        });
    }
});

const crmAuthEnabled = Boolean(window.crmAuthEnabled);
const eauthEnabled = Boolean(window.eauthEnabled);
const eauthUrl = typeof window.eauthUrl === "string" ? window.eauthUrl : "";
const showAlternativeLogins = computed(() => crmAuthEnabled || eauthEnabled);

const form = ref({
    email: "",
    password: "",
});

const rules = {
    email: [
        { required: true, message: "O e-mail é obrigatório.", trigger: "blur" },
        { type: "email", message: "Digite um e-mail válido.", trigger: "blur" },
    ],
    password: [
        { required: true, message: "A senha é obrigatória.", trigger: "blur" },
        { min: 6, message: "A senha deve ter no mínimo 6 caracteres.", trigger: "blur" },
    ],
};

const handleSubmit = async () => {
    if (!(await isLocationActive())) {
        notification.error({
            message: "Erro ao fazer login",
            description: "Você precisa ativar a permissão de localização.",
        });
        return
    }
    try {
        await formRef.value.validate();
        form.value.coords = await getCoords();
        loading.value = true;

        const response = await window.axios.post("/api/login", form.value);
        notification.success({
            message: "Login realizado com sucesso!",
        });

        window.location.href = "/home"; // Redireciona após login
    } catch (error) {
        notification.error({
            message: "Erro ao fazer login",
            description: "Verifique suas credenciais.",
        });
    } finally {
        loading.value = false;
    }
};

function loginWithCrm() {
    window.location.href = "/crm/auth";
}

function loginWithEauth() {
    if (!eauthUrl) {
        return;
    }
    window.location.href = eauthUrl;
}
</script>

<style scoped>
.login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
}

.login-card {
    width: 400px;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
}

.alternative-logins {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
</style>
